import React, { useState } from 'react';
import { useModel } from '../ModelProvider';
import { detailsText, copyText, DetailsBlock, detailsButtonStyle, detailsPrimaryButtonStyle } from './ErrorBoundary';

/* ★ CRASHROOT PART 2 — THE NAMED ERROR STATE.
 *
 * Sibling of OnboardingIncomplete, and deliberately a SEPARATE component: that one
 * names an ONBOARDING GAP (the tenant has not finished the wizard — the operator has
 * something to do, and it says what). This one names a LOAD FAILURE (the fetch or the
 * compute threw — the data may be perfectly fine). Folding a network error into the
 * onboarding copy would tell the operator to go and fix data that is not broken.
 *
 * ONE implementation, imported by every reader, rather than an error <div> copied per
 * page (Principle 0). Display-only: no engine call, no result field, no number.
 *
 * ⛔ NO RETRY BUTTON IN THIS BUILD — deliberate, not an omission. A browser refresh
 *    remounts ModelProvider (App.js keys it on the company id) and replays the whole
 *    load, which IS the recovery path; wiring an in-page retry is new behaviour with
 *    its own blast radius and is filed as a later build.
 * NOTE (W-025): that still holds for the LOAD kind. The COMPUTE kind (ComputeError, below) does carry a
 *    Retry, and it is the provider's existing refresh(), not a new mechanism.
 */

const FONT = "'DM Sans',-apple-system,sans-serif";
const MONO = "'Space Mono',monospace";

/* W-025 -- THE "COULD NOT BE CALCULATED" KIND. The provider records WHY the model is in 'error'
 * (modelErrorInfo.kind): 'load' = the fetch failed (network / server -- the copy below, unchanged);
 * 'compute' = the data arrived and the MODEL threw, on the load or on a recalculation (a cash sweep or
 * balance sheet assumptions change). A compute failure must not blame the connection, and it must not
 * leave the previous numbers on screen as though they were current.
 * Retry calls the provider's existing refresh() -- the same full load a page runs on mount; no new
 * network path. The details block is the page boundary's own (ErrorBoundary.jsx). */
function ComputeError({ message, info, context, onRetry }) {
  const [copied, setCopied] = useState(false);
  const text = detailsText({ page: context.page, company: context.company, at: info.at, message });
  return (
    <div role="alert" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', padding: '60px 24px', textAlign: 'center', fontFamily: FONT,
    }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>{String.fromCharCode(0x26A0)}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#1A1C2E', marginBottom: 12 }}>
        The model could not be calculated for this company.
      </div>
      <div style={{ fontSize: 14, color: '#5A5E73', maxWidth: 560, lineHeight: 1.6 }}>
        Your data has not been changed.{info.trigger ? ' This happened while ' + info.trigger + '.' : ''} Try
        again, or choose another page from the menu. If it keeps happening, send the details below to support.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        {onRetry ? <button type="button" style={detailsPrimaryButtonStyle} onClick={onRetry}>Retry</button> : null}
        <button type="button" style={detailsButtonStyle} onClick={() => copyText(text, () => setCopied(true))}>{copied ? 'Copied' : 'Copy details'}</button>
      </div>
      <DetailsBlock text={text} />
    </div>
  );
}

export default function ModelLoadError({ message }) {
  const model = useModel();
  const info = model && model.modelErrorInfo;
  if (info && info.kind === 'compute') {
    return <ComputeError message={message} info={info} context={model.errorContext || {}} onRetry={model.refresh || null} />;
  }
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', padding: '60px 24px', textAlign: 'center', fontFamily: FONT,
    }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>{'\u26A0'}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#1A1C2E', marginBottom: 12 }}>
        Couldn{'\u2019'}t load this company{'\u2019'}s model
      </div>
      <div style={{ fontSize: 14, color: '#5A5E73', maxWidth: 560, lineHeight: 1.6 }}>
        The model data failed to load, so no statements were computed. This is a
        connection or server problem, not a problem with your data {'\u2014'} nothing has been
        changed. Refresh the page to try again.
      </div>
      {message ? (
        <div style={{ marginTop: 18, fontFamily: MONO, fontSize: 11, color: '#8B8FA3', maxWidth: 560, wordBreak: 'break-word' }}>
          {message}
        </div>
      ) : null}
    </div>
  );
}
